/**
 * Procedural DEVELOPMENT placeholder tileset + fallback layer painter (owner: env-builder). Used only
 * by the `?showcase=world` grey box; final art comes from the asset pipeline. Labels are Latin
 * letters — Chinese is never baked into images (ART_DIRECTION.md). Character stand-ins come from the
 * characters module (`createActor` generates its own labelled placeholder when a texture is missing).
 */
import Phaser from 'phaser';
import { Depth, TILE } from '@/core/config';
import type { MapData } from '@/core/types/contracts';
import { registerTileset } from '@/maps';
import { DEV_TILES, DEV_TILESET_KEY, EMPTY_TILE } from './devMap';

function hex(c: number): string { return `#${c.toString(16).padStart(6, '0')}`; }
function shade(c: number, f: number): number {
  const r = Math.min(255, Math.round(((c >> 16) & 0xff) * f)), g = Math.min(255, Math.round(((c >> 8) & 0xff) * f)), b = Math.min(255, Math.round((c & 0xff) * f));
  return (r << 16) | (g << 8) | b;
}

/** 9 × 32px dev tiles in one row; frame `i` = DEV_TILES[i]. Registers the tileset id. Idempotent. */
export function ensureDevTileset(scene: Phaser.Scene): boolean {
  registerTileset({ id: DEV_TILESET_KEY, texture: DEV_TILESET_KEY, tileWidth: TILE, tileHeight: TILE });
  if (scene.textures.exists(DEV_TILESET_KEY)) return true;
  const tex = scene.textures.createCanvas(DEV_TILESET_KEY, TILE * DEV_TILES.length, TILE);
  if (!tex) { console.warn('[world] could not create dev tileset canvas'); return false; }
  const c = tex.context;
  DEV_TILES.forEach((t, i) => {
    const x = i * TILE;
    const transparent = t.name === 'tuft';
    if (!transparent) {
      c.fillStyle = hex(t.color);
      c.fillRect(x, 0, TILE, TILE);
      c.fillStyle = hex(shade(t.color, 0.78));
      c.fillRect(x, TILE - 1, TILE, 1);
      c.fillRect(x + TILE - 1, 0, 1, TILE);
      c.fillStyle = hex(shade(t.color, 1.12));
      c.fillRect(x, 0, TILE, 1);
      c.fillRect(x, 0, 1, TILE);
    }
    switch (t.name) {
      case 'water':
        c.fillStyle = hex(shade(t.color, 1.35));
        for (let k = 0; k < 3; k++) c.fillRect(x + 4 + k * 9, 8 + k * 8, 6, 1);
        break;
      case 'wall':
        c.fillStyle = hex(shade(t.color, 0.7));
        for (let row = 0; row < 4; row++) { c.fillRect(x, row * 8 + 7, TILE, 1); c.fillRect(x + ((row % 2) * 8), row * 8, 1, 8); c.fillRect(x + 16 + ((row % 2) * 8), row * 8, 1, 8); }
        break;
      case 'crate':
        c.strokeStyle = hex(shade(t.color, 0.6)); c.lineWidth = 2;
        c.beginPath(); c.moveTo(x + 3, 3); c.lineTo(x + TILE - 3, TILE - 3); c.moveTo(x + TILE - 3, 3); c.lineTo(x + 3, TILE - 3); c.stroke();
        break;
      case 'roof':
        c.fillStyle = hex(shade(t.color, 1.3));
        for (let k = 0; k < 4; k++) c.fillRect(x, k * 8 + 2, TILE, 2);
        break;
      case 'pier':
        c.fillStyle = hex(shade(t.color, 0.7));
        for (let k = 0; k < 4; k++) c.fillRect(x, k * 8, TILE, 1);
        break;
      case 'tuft':
        c.strokeStyle = hex(t.color); c.lineWidth = 1;
        c.beginPath(); c.moveTo(x + 12, 24); c.lineTo(x + 14, 16); c.moveTo(x + 16, 24); c.lineTo(x + 16, 14); c.moveTo(x + 20, 24); c.lineTo(x + 18, 17); c.stroke();
        break;
      default: break;
    }
    c.fillStyle = 'rgba(0,0,0,0.45)';
    c.font = '8px monospace';
    c.textBaseline = 'bottom';
    c.fillText(t.label, x + 2, TILE - 2);
    tex.add(i, 0, x, 0, TILE, TILE);
  });
  tex.refresh();
  return true;
}

export interface DevMapVisuals { images: Phaser.GameObjects.Image[]; missingTilesets: string[] }

/**
 * Fallback renderer: paints each MapLayerData into one canvas texture per layer and places it at
 * the right depth band. Used only when `createMapRuntime` (maps builder) drew nothing.
 * Layer values: -1 = empty, otherwise a 0-based frame index (tiles read left→right, top→bottom).
 */
export function drawDevMapLayers(scene: Phaser.Scene, map: MapData, overhead: Phaser.GameObjects.Layer): DevMapVisuals {
  const images: Phaser.GameObjects.Image[] = [];
  const missing: string[] = [];
  for (const layer of map.layers) {
    const tsKey = layer.tileset;
    if (!scene.textures.exists(tsKey)) { if (!missing.includes(tsKey)) missing.push(tsKey); continue; }
    const src = scene.textures.get(tsKey);
    const source = src.getSourceImage() as HTMLCanvasElement | HTMLImageElement;
    const key = `dev-map-${map.id}-${layer.name}`;
    if (!scene.textures.exists(key)) {
      const tex = scene.textures.createCanvas(key, map.width * map.tileSize, map.height * map.tileSize);
      if (!tex) continue;
      const c = tex.context;
      const perRow = Math.max(1, Math.floor((src.source[0]?.width ?? map.tileSize) / map.tileSize));
      for (let ty = 0; ty < map.height; ty++) {
        for (let tx = 0; tx < map.width; tx++) {
          const f = layer.data[ty * map.width + tx] ?? EMPTY_TILE;
          if (f < 0) continue;
          const sx = (f % perRow) * map.tileSize, sy = Math.floor(f / perRow) * map.tileSize;
          c.drawImage(source, sx, sy, map.tileSize, map.tileSize, tx * map.tileSize, ty * map.tileSize, map.tileSize, map.tileSize);
        }
      }
      tex.refresh();
    }
    const img = scene.add.image(0, 0, key).setOrigin(0);
    if (layer.depth === 'overhead') overhead.add(img);
    else img.setDepth(layer.depth === 'ground' ? Depth.GROUND : Depth.GROUND_DETAIL);
    images.push(img);
  }
  for (const m of missing) console.warn(`[world] missing tileset texture "${m}" for map ${map.id}; layer skipped`);
  return { images, missingTilesets: missing };
}
